import { useEffect } from "react";

const SITE_URL = "https://nawadah.com";
const BRAND_NAME = "Nawadah by NDH Official";
const TITLE_SUFFIX = ` | ${BRAND_NAME}`;
const stripBrandSuffix = (title) => title.replace(/ \| (?:NAWADAH|Nawadah by NDH Official)$/, "");

export const buildPageSchema = (title, description, path, options = {}) => {
  const canonical = `${SITE_URL}${path}`;
  const crumbs = [{ "@type": "ListItem", position: 1, name: "Beranda", item: `${SITE_URL}/` }];
  if (path.startsWith("/blog/")) {
    crumbs.push({ "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` });
  }
  if (path !== "/") {
    crumbs.push({ "@type": "ListItem", position: crumbs.length + 1, name: stripBrandSuffix(title), item: canonical });
  }
  const graph = [{
    "@type": "WebPage", "@id": `${canonical}#webpage`, url: canonical,
    name: title, description, inLanguage: "id-ID", isPartOf: { "@id": `${SITE_URL}/#website` },
  }];
  if (crumbs.length > 1) graph.push({ "@type": "BreadcrumbList", itemListElement: crumbs });
  if (options.type === "article" && !options.noindex) {
    graph.push({
      "@type": "BlogPosting", "@id": `${canonical}#article`,
      headline: stripBrandSuffix(title), description,
      mainEntityOfPage: { "@id": `${canonical}#webpage` },
      publisher: { "@id": `${SITE_URL}/#organization` },
      ...(options.image ? { image: options.image } : {}),
      ...(options.publishedTime ? { datePublished: options.publishedTime } : {}),
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
};

const setMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
};

export const usePageMeta = (title, description, options = {}) => {
  const path = window.location.pathname === "/" ? "/" : window.location.pathname.replace(/\/+$/, "");
  const canonicalPath = path === "/" ? "/" : `${path}/`;
  useEffect(() => {
    const fullTitle = options.absoluteTitle
      ? title
      : title
        ? `${title}${TITLE_SUFFIX}`
        : `${BRAND_NAME} | Supplier & Distributor Industri`;
    const canonicalUrl = `${SITE_URL}${canonicalPath}`;

    document.title = fullTitle;
    if (description) {
      setMeta('meta[name="description"]', { name: "description", content: description });
      setMeta('meta[property="og:description"]', { property: "og:description", content: description });
      setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    }

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    setMeta('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    setMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    setMeta('meta[property="og:type"]', { property: "og:type", content: options.type || "website" });
    setMeta('meta[property="og:image"]', { property: "og:image", content: options.image || `${SITE_URL}/media/1db42203-3aac-436c-9071-54a41f0d5b3d.png` });
    setMeta('meta[name="robots"]', { name: "robots", content: options.noindex ? "noindex, follow" : "index, follow" });

    // Keep route-specific schema in sync during SPA navigation as well as direct loads.
    let schema = document.getElementById("route-seo-schema");
    if (!schema) {
      schema = document.createElement("script");
      schema.id = "route-seo-schema";
      schema.type = "application/ld+json";
      document.head.appendChild(schema);
    }
    schema.textContent = JSON.stringify(buildPageSchema(fullTitle, description, canonicalPath, {
      type: options.type, image: options.image, publishedTime: options.publishedTime, noindex: options.noindex,
    })).replace(/</g, "\\u003c");
  }, [title, description, canonicalPath, options.absoluteTitle, options.type, options.image, options.publishedTime, options.noindex]);
};
